<script setup lang="ts">
import { useLocationResolver } from "@/composable/location-resolver";
import { BaseColor, Button, ButtonProps, Icon, LinkButton, Size, Tooltip } from "@soft-db/ui";
import { ButtonHTMLAttributes } from "vue";
import { RouteLocationRaw, RouterLinkProps } from "vue-router";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
	defineProps<{
		title: string;
		to: RouteLocationRaw;
		icon: string;
		color: BaseColor;
		size?: Size;
		ghost?: boolean;
		disabled?: boolean;
		disabledReason?: string;
	}>(),
	{
		size: "sm",
	}
);

const location = useLocationResolver();
const to = computed(() => location.resolve(props.to));

const buttonIs = computed(() => {
	if (props.disabled) {
		return Button;
	} else {
		return LinkButton;
	}
});

const buttonProps = computed(() => {
	const baseProps: ButtonProps & ButtonHTMLAttributes = {
		size: props.size,
		color: props.color,
		variant: props.ghost ? "ghost" : "soft",
	};

	if (props.disabled) {
		baseProps.disabled = true;
		return baseProps;
	} else {
		const routeProps: ButtonProps & RouterLinkProps = { ...baseProps, to: props.to };
		return routeProps;
	}
});

const tooltip = computed(() => {
	if (props.disabled) {
		return props.disabledReason ?? props.title;
	} else {
		return props.title;
	}
});
</script>

<template>
	<Tooltip v-if="to" :tooltip="tooltip">
		<span>
			<component :is="buttonIs" v-bind="{ ...buttonProps, ...$attrs }">
				<Icon :d="icon" />
			</component>
		</span>
	</Tooltip>
</template>
